import data from "../../shared/sharedData";
import moment from "moment";
export default {
  name: "Employer Information",
  symbol: "list",
  pageInfo: {
    text: `<b><u>Note:</u></b> The information entered in this section must be the same as the employer information stated for the employer’s job opportunity on the prevailing wage determination (PWD) for the Form ETA-9141, <i>Application for Prevailing Wage Determination</i>, issued by the Department.`,
    coloring: "grey",
  },
  sections: [
    {
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer profile to populate this section (optional)",
          prefill: {
            schema: "employerProfiles",
            type: "employer",
            formName: "9089",
            formSection: "A",
          },
        },
        {
          code: "A.1",
          machine_name: "empBusinessName",
          widget: "text",
          label: "Legal Business Name",
          validate: ["required", "noNAs"],
          maxLength: 120,
          tooltip: `Enter the full name of the individual employer, partnership, corporation (i.e., the employer filing this application).  The employer’s full legal business name is the exact name of the individual, corporation, LLC, partnership, or other organization that is reported to the Internal Revenue Service (IRS).`,
        },
        {
          code: "A.2",
          machine_name: "empTradeName",
          widget: "text",
          label: "Trade Name/Doing Business As (DBA), if applicable §",
          maxLength: 120,
          tooltip: `Enter the full trade name or “Doing Business As” (DBA) name, if applicable, of the business, person, association, firm, corporation, or organization (i.e., the employer filing this application).  If no trade name or “Doing Business As” (DBA) name, enter “N/A.”`,
        },
        {
          code: "A.3",
          machine_name: "empAddr1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Address 1 must contain at least 2 characters.",
            },
            "noNAs",
          ],
          tooltip: `Enter the street address of the employer’s principal place of business.  The place of business must be a physical location and not a Post Office (P.O.) Box.`,
          maxLength: 60,
        },
        {
          code: "A.4",
          machine_name: "empAddr2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this field to complete the employer’s street address.  If no additional space is needed, enter “N/A.”`,
        },
        {
          code: "A.8",
          label: "Country",
          machine_name: "empCountry",
          widget: "select",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["empState", "empProvince"],
          tooltip: `Enter the country of the employer’s principal place of business.`,
        },
        {
          code: "A.5",
          machine_name: "empCity",
          widget: "text",
          label: "City",
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
            "noNAs",
          ],
          tooltip: `Enter the city of the employer’s principal place of business. If the city and country are the same, the name <u>must</u> still be entered in <u>both</u> fields.`,
          maxLength: 35,
        },
        {
          code: "A.6",
          label: "State",
          machine_name: "empState",
          widget: "select",
          selection: data.state,
          state: true,
          maxLength: 35,
          disabled: true,
          disableWhenField: "empCountry",
          isVisible: {
            when: "empCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            "required",
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip: `Enter the state, district, or territory of the employer’s principal place of business.`,
        },
        {
          code: "A.7",
          machine_name: "empPostcode",
          widget: "text",
          label: "Postal Code",
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            {
              postalCountry: "empCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "empCountry",
              },
            },
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip: `Enter the postal (zip) code of the employer’s principal place of business.`,
          maxLength: 10,
        },
        {
          code: "A.9",
          machine_name: "empProvince",
          widget: "text",
          label: "Province §",
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip: `Enter the province of the employer’s principal place of business, if applicable. Enter “N/A” if not applicable.`,
          maxLength: 25,
        },
        {
          code: "A.10",
          label: "Telephone Number",
          machine_name: "empPhone",
          widget: "phone",
          validate: ["required", "phone"],
          tooltip: `Enter the area code and telephone number for the employer’s principal place of business. Include country code, if outside of the United States.`,
        },
        {
          code: "A.11",
          label: "Extension §",
          machine_name: "empPhoneext",
          widget: "text",
          maxLength: 4,
          tooltip: `Enter the extension of the telephone number for the employer’s principal place of business, if applicable.  Enter “N/A” if not applicable.`,
        },
        {
          code: "A.12",
          label: "Federal Employer Identification Number (FEIN from IRS) ",
          machine_name: "empFein",
          widget: "text",
          validate: [
            "required",
            "fein",
            { minLength: 10, message: "This is not a valid FEIN" },
            {
              FEINPrefixValidator: "empFein",
              needsFieldValues: true,
              message: "This is not a valid FEIN",
            },
          ],
          tooltip: `Enter the nine-digit Federal Employer Identification Number (FEIN), as assigned by the IRS. 
          <b><u>Do not enter a social security number</u></b>.  All employers, <b><u>including private households</u></b>, 
          MUST obtain an FEIN from the IRS before completing this application.  Information on obtaining an 
          FEIN can be found at <u><a href="https://www.irs.gov" target="_blank">www.irs.gov</a></u>.`,
          parse: "fein",
          maxLength: 10,
        },
        {
          code: "A.13",
          machine_name: "empNaicsDesc",
          widget: "text",
          label: "NAICS Description",
          hidden: true,
        },
        {
          code: "A.13",
          machine_name: "empNaics",
          widget: "naics",
          label: "NAICS Code",
          field_to_modify: "empNaicsDesc",
          validate: ["required"],
          tooltip: `Enter the four-digit North American Industry Classification System (NAICS) code that best describes the employer’s business, not the PERM \
           job opportunity. A listing of NAICS codes can be found at <u><a href="https://www.census.gov/naics/" target="_blank">https://www.census.gov/naics/</a></u>.`,
        },
        {
          code: "A.14",
          machine_name: "empNumPayroll",
          widget: "text",
          label:
            "Number of current employees on payroll in the area of intended employment",
          validate: [
            "required",
            "number",
            {
              minLength: 1,
              message: "",
            },
          ],
          tooltip: `Enter the number of employees currently on the employer’s payroll (i.e., employees employed by the employer) in the area of intended employment.  The area of intended employment is the area within normal commuting distance of the address of the place of intended employment.`,
          maxLength: 60,
        },
        {
          code: "A.15",
          machine_name: "empYearCommenced",
          widget: "text",
          label: "Year Commenced Business ( if household, year issued FEIN)",
          maxLength: 4,
          validate: [
            "required",
            "number",
            {
              minLength: 4,
              message: "Please enter year commenced business in 'yyyy' format",
            },
            {
              maxLength: 4,
              message: "Please enter year commenced business in 'yyyy' format",
            },
            {
              maxValue: moment().year(),
              message: `Year Commenced Business cannot be after ${moment().year()}`,
            },
          ],
          tooltip: `Enter the year the employer began business or was incorporated.  If the employer is a private household, enter the year the IRS issued a FEIN.  Use a full year (YYYY) format.`,
        },
        {
          isGroup: {
            code: "A.16",
            machine_name: "empWorkerInterest",
            required: true,
            label: `Is the employer a closely held corporation, partnership, or sole proprietorship in which the foreign worker has an ownership interest?`,
            tooltip:
              "If the employer is a closely held corporation, partnership, or sole proprietorship in which the foreign worker has an ownership interest, mark “Yes.”  Otherwise, mark “No.”  A closely held corporation is typically one with relatively few shareholders and whose shares are not generally traded in the securities market.",
            coloring: "none",
            clearBtn: {
              clearSchema: ["empWorkerInterest"],
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "Yes",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "No",
                validate: ["required"],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "A.17",
            machine_name: "empRelationshipWorker",
            required: true,
            label: `Is there a familial relationship between the foreign worker and the owners, stockholders, partners, corporate officers, and/or incorporators?`,
            tooltip: `If there is a familial relationship between the foreign worker and the owners, stockholders, partners, corporate officers, and/or incorporators of the employer, mark “Yes.”  Otherwise, mark “No.”`,
            coloring: "none",
            clearBtn: {
              clearSchema: ["empRelationshipWorker"],
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "Yes",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "No",
                validate: ["required"],
              },
            ],
          },
        },
      ],
    },
  ],
};
